import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { parseChatError } from "../../../shared/chatErrors";
import { SUB_AGENT_UPDATE_PREFIX } from "../../../shared/subAgent";
import type { StatusState } from "../../lib/chatStatus";

// Tool calls that render as their own transcript row as well as inside the
// activity group.
export const STANDALONE_TOOLS: ReadonlySet<string> = new Set([
  "approvePlan",
  "seedBranchStorage",
]);

export type GroupedEntry =
  | {
      type: "single";
      entry: ChatEntryWithSubTables;
      interrupted?: boolean;
    }
  | {
      type: "userActionGroup";
      entries: ChatEntryWithSubTables[];
      groupId: string;
    }
  | {
      type: "toolGroup";
      entries: ChatEntryWithSubTables[];
      groupId: string;
      activePromptId?: string;
      isTrailing?: boolean;
      responseStarted?: boolean;
    };

export function groupChatEntries(
  entries: readonly ChatEntryWithSubTables[],
  statusState: StatusState,
  activeChatId?: string | null,
  activePromptId?: string
): GroupedEntry[] {
  const result: GroupedEntry[] = [];
  let currentActivity: ChatEntryWithSubTables[] = [];
  let currentUserActions: ChatEntryWithSubTables[] = [];
  let activityGroupIndex = 0;
  let activityStartIndex = 0;
  let responseStarted = false;

  const flushUserActions = () => {
    const first = currentUserActions[0];
    if (!first) return;
    result.push({
      type: "userActionGroup",
      entries: currentUserActions,
      groupId: first.id,
    });
    currentUserActions = [];
  };

  const flushActivity = (trailing = false) => {
    const first = currentActivity.find(
      (entry) => entry.entryType !== "AssistantMessage"
    );
    const isTrailing =
      trailing &&
      first !== undefined &&
      (activeChatId == null || first.chatId === activeChatId) &&
      statusState !== "resting" &&
      (statusState !== "waiting" ||
        activePromptId === undefined ||
        currentActivity.some((entry) => entry.id === activePromptId));
    const opening = currentActivity.find(
      (entry) =>
        entry.entryType === "AssistantMessage" ||
        entry.entryType === "AssistantToolUse"
    );
    const last = currentActivity.at(-1);
    const response =
      last?.toolCall?.toolName === "pushLive"
        ? currentActivity.findLast(
            (entry) =>
              entry.entryType === "AssistantMessage" &&
              entry !== opening &&
              !parseChatError(entry.assistantMessage?.text ?? "")
          )
        : last;
    const history = currentActivity.filter(
      (entry) =>
        entry.entryType !== "AssistantMessage" ||
        (entry !== response &&
          entry !== opening &&
          !parseChatError(entry.assistantMessage?.text ?? ""))
    );
    currentActivity = [];
    if (!first) {
      activityStartIndex = result.length;
      responseStarted = false;
      return;
    }
    const nestedMessages = new Set(
      history
        .filter((entry) => entry.entryType === "AssistantMessage")
        .map((entry) => entry.id)
    );
    for (let index = result.length - 1; index >= activityStartIndex; index--) {
      const grouped = result[index]!;
      if (grouped.type !== "single") continue;
      if (nestedMessages.has(grouped.entry.id)) {
        result.splice(index, 1);
        if (index < activityGroupIndex) activityGroupIndex--;
      } else if (
        !isTrailing &&
        grouped.entry.toolCall &&
        grouped.entry.toolCall.completedAt == null
      ) {
        grouped.interrupted = true;
      }
    }
    const responseIndex =
      response?.entryType === "AssistantMessage"
        ? result.findIndex(
            (grouped) => grouped.type === "single" && grouped.entry === response
          )
        : -1;
    result.splice(
      responseIndex >= 0
        ? responseIndex
        : isTrailing
          ? result.length
          : activityGroupIndex,
      0,
      {
        type: "toolGroup",
        entries: history,
        groupId: first.id,
        ...(trailing && { isTrailing }),
        ...(isTrailing && activePromptId && { activePromptId }),
        ...(isTrailing && responseStarted && { responseStarted: true }),
      }
    );
    activityStartIndex = result.length;
    responseStarted = false;
  };

  for (const [index, entry] of entries.entries()) {
    if (
      entry.entryType === "UserMessage" ||
      entry.entryType === "UserInterrupt" ||
      entry.systemMessage?.text.startsWith(SUB_AGENT_UPDATE_PREFIX) ||
      (index > 0 && entries[index - 1]!.chatId !== entry.chatId)
    ) {
      flushUserActions();
      flushActivity();
    }
    if (entry.entryType === "UserInterrupt") continue;

    if (
      entry.entryType === "UserAction" &&
      entry.userAction?.actionType !== "pushDraftToLive"
    ) {
      currentUserActions.push(entry);
      continue;
    }
    flushUserActions();

    if (
      (entry.entryType === "AssistantToolUse" && entry.toolCall) ||
      (entry.entryType === "AssistantReasoning" && entry.assistantReasoning)
    ) {
      currentActivity.push(entry);
      responseStarted = false;
      if (entry.toolCall && STANDALONE_TOOLS.has(entry.toolCall.toolName)) {
        result.push({ type: "single", entry });
      }
      activityGroupIndex = result.length;
    } else {
      result.push({ type: "single", entry });
      if (entry.entryType === "AssistantMessage") {
        currentActivity.push(entry);
        responseStarted = true;
      }
    }
  }

  flushUserActions();
  flushActivity(true);
  return result;
}
